import { keepPreviousData } from "@tanstack/react-query";
import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { Badge, Button, Headline, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { useLearnerEhrSyncsQuery } from "@api/graphql/types-and-hooks";
import { EhrStoredRowTable } from "@pages/ClientEhrProgramming/EhrStoredRowTable";

export const SYNC_HISTORY_PAGE_SIZE = 25;

// Every EHR read recorded for this client (EhrSync rows), newest first.
export const ClientEhrSyncHistory: React.FC = () => {
  const { clientId } = useParams();
  const [offset, setOffset] = useState(0);

  const { data, isLoading, isFetching, isError } = useLearnerEhrSyncsQuery(
    { learnerId: clientId!, offset, limit: SYNC_HISTORY_PAGE_SIZE },
    {
      enabled: !!clientId,
      queryKey: ["learner-ehr-syncs", clientId, offset],
      placeholderData: keepPreviousData,
    },
  );

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Paragraph colorType="secondary">Loading...</Paragraph>
      </div>
    );
  }

  if (isError || !data?.learnerEhrSyncs) {
    return (
      <div className="flex justify-center p-8">
        <Paragraph colorType="secondary">Couldn&apos;t load sync history.</Paragraph>
      </div>
    );
  }

  const { totalCount, rows } = data.learnerEhrSyncs;
  const lastShown = Math.min(offset + SYNC_HISTORY_PAGE_SIZE, totalCount);

  return (
    <div className="flex flex-col gap-4 w-full p-4">
      <div className="flex flex-col gap-1">
        <Headline size="small">Sync History</Headline>
        <Paragraph size="small" colorType="secondary">
          Every read we recorded from this client&apos;s EHR, newest first, with the folder its data was saved to.
        </Paragraph>
      </div>
      {rows.length ? (
        <div className="flex flex-col gap-3 p-5 w-full bg-white rounded-lg border border-limestone-200">
          <EhrStoredRowTable
            rows={rows.map((row) => ({
              id: row.id,
              fields: [
                { key: "started_at", value: row.startedAt },
                { key: "ehr_name", value: row.ehrName },
                { key: "run_folder", value: row.runFolder },
                { key: "temporal_workflow_id", value: row.temporalWorkflowId },
              ],
            }))}
          />
          <div className="flex items-center gap-3">
            <Small className="text-text-secondary">
              {offset + 1}–{lastShown} of {totalCount}
            </Small>
            <Button
              variant="primary"
              buttonStyle="border"
              text="Newer"
              disabled={offset === 0 || isFetching}
              onClick={() => setOffset(Math.max(0, offset - SYNC_HISTORY_PAGE_SIZE))}
            />
            <Button
              variant="primary"
              buttonStyle="border"
              text="Older"
              disabled={lastShown >= totalCount || isFetching}
              onClick={() => setOffset(offset + SYNC_HISTORY_PAGE_SIZE)}
            />
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center gap-2 p-8">
          <Badge appearance="neutral" text="None yet" />
          <Paragraph colorType="secondary">No EHR reads recorded for this client.</Paragraph>
        </div>
      )}
    </div>
  );
};
